/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import configuration */
import {
    HE_CUTOFF_TEMPERATURE_CRITICAL,
    HE_CUTOFF_TEMPERATURE_OK,
    HE_CUTOFF_TEMPERATURE_WARNING,
    HE_WEEKEND_REDUCTION_CRITICAL,
    HE_WEEKEND_REDUCTION_WARNING,
} from '@/config/app';

/* Import libraries */
import {DeviceSensor} from "@/lib/sensors/catalog/deviceSensor";
import { roundToSignificantDigits } from '@/lib/formatting/significantDigits';
import { getHeatingEnergySavedEnergyWarningPercentage } from '@/lib/heatingEnergy/savedEnergyStatus';
import type {
    KpiCarouselTrafficLightContext,
} from '@/components/api/KpiCarouselApi2TimeSeries';
import type { TimeSeriesTrafficLightConfig } from '@/lib/timeSeries/timeSeriesValue';

type Translate = (key: string, values?: Record<string, number | string>) => string;

interface HeatingEnergyDetailKpiConfigOptions {
    deviceSensor: DeviceSensor;
    t: Translate;
}

function createSavedEnergyTrafficLight(
    periodStart: string | null,
    periodEnd: string | null,
): TimeSeriesTrafficLightConfig {
    return {
        rules: [
            { severity: 'danger', value: 0, when: 'lt' },
            {
                severity: 'warning',
                value: getHeatingEnergySavedEnergyWarningPercentage(periodStart, periodEnd) / 100,
                when: 'lt',
            },
        ],
        fallback: 'success',
    };
}

function getSavedEnergyRatio(
    savedEnergy: number,
    energyAmountTrain: number | null,
): number | null {
    if (energyAmountTrain === null || energyAmountTrain === 0) {
        return null;
    }

    return savedEnergy / energyAmountTrain;
}

/**
 * Creates the heating-energy KPI definitions displayed above the prediction chart.
 */
export function createHeatingEnergyDetailKpiConfigs({
    deviceSensor,
    t,
}: HeatingEnergyDetailKpiConfigOptions) {

    const cutoffTemperatureSensorName = deviceSensor.getSensorName('HE', 'CutoffTemperature');
    const weekendReductionPercentSensorName = deviceSensor.getSensorName('HE', 'WeekendReductionPercent');
    const savedEnergySensorName = deviceSensor.getSensorName('HE', 'SavedEnergy');
    const energyAmountTrainSensorName = deviceSensor.getSensorName('HE', 'EnergyAmountTrain');
    const trainingEnd = deviceSensor.getTrainingEnd('HE');

    if (
        cutoffTemperatureSensorName === null ||
        weekendReductionPercentSensorName === null ||
        savedEnergySensorName === null ||
        energyAmountTrainSensorName === null
    ) {
        return null;
    }

    return {
        /* KpiValueApi2TimeSeries: Cutoff temperature */
        cutoffTemperature: {
            aggregation: 'last' as const,
            conversion: 1,
            fractionDigits: 1,
            from: 'NOW-4WEEKS',
            showDate: true,
            showLegend: true,
            title: t('Cutoff temperature'),
            to: 'NOW',
            trafficLight: {
                rules: [
                    { severity: 'danger' as const, value: HE_CUTOFF_TEMPERATURE_CRITICAL, when: 'gte' as const },
                    { severity: 'warning' as const, value: HE_CUTOFF_TEMPERATURE_WARNING, when: 'gte' as const },
                    { severity: 'success' as const, value: HE_CUTOFF_TEMPERATURE_OK, when: 'gte' as const },
                ],
                fallback: 'implausible' as const,
            },
            unit: '°C',
            urn: cutoffTemperatureSensorName,
        },

        /* KpiValueApi2TimeSeries: Weekend reduction percent */
        weekendReductionPercent: {
            aggregation: 'last' as const,
            conversion: 1,
            from: 'NOW-4WEEKS',
            showDate: true,
            showLegend: true,
            title: t('Weekend reduction'),
            to: 'NOW',
            trafficLight: {
                rules: [
                    { severity: 'danger' as const, value: HE_WEEKEND_REDUCTION_CRITICAL, when: 'lt' as const },
                    { severity: 'warning' as const, value: HE_WEEKEND_REDUCTION_WARNING, when: 'lt' as const },
                ],
                fallback: 'success' as const,
            },
            transformValue: (value: number) => roundToSignificantDigits(value, 2),
            unit: '%',
            urn: weekendReductionPercentSensorName,
        },

        /* KpiCarouselApi2TimeSeries: Saved energy */
        savedEnergy: {
            autoAdvanceMs: 8000,
            // Allows a yearly comparison even when the most recent API value is delayed.
            from: 'NOW-25MONTHS',
            showDate: true,
            showLegend: true,
            to: 'NOW',
            items: [
                {
                    aggregation: 'last' as const,
                    conversion: 1 / 1000,
                    fractionDigits: 2,
                    key: 'saved_energy_overall',
                    legendConversion: 100,
                    legendFractionDigits: 1,
                    legendUnit: '%',
                    resolveTrafficLight: ({ valueDate }: KpiCarouselTrafficLightContext) => (
                        createSavedEnergyTrafficLight(trainingEnd, valueDate)
                    ),
                    resolveTrafficLightValue: ({ value, values }: KpiCarouselTrafficLightContext) => (
                        getSavedEnergyRatio(value, values[energyAmountTrainSensorName] ?? null)
                    ),
                    title: t('Saved energy overall'),
                    transformValue: (value: number) => roundToSignificantDigits(value, 2),
                    trimTrailingZeros: true,
                    unit: 'MWh',
                    urn: [savedEnergySensorName, energyAmountTrainSensorName],
                },
                {
                    aggregation: 'last' as const,
                    conversion: 1 / 1000,
                    fractionDigits: 2,
                    key: 'saved_energy_last_year',
                    legendConversion: 100,
                    legendFractionDigits: 1,
                    legendUnit: '%',
                    lookback: { amount: 1, toleranceDays: 31, unit: 'year' as const },
                    noDataMessage: t('No historical comparison value is available.'),
                    resolveTrafficLight: ({ comparisonDate, valueDate }: KpiCarouselTrafficLightContext) => (
                        createSavedEnergyTrafficLight(comparisonDate, valueDate)
                    ),
                    resolveTrafficLightValue: ({ value, values }: KpiCarouselTrafficLightContext) => (
                        getSavedEnergyRatio(value, values[energyAmountTrainSensorName] ?? null)
                    ),
                    title: t('Saved energy last year'),
                    transformValue: (value: number) => roundToSignificantDigits(value, 2),
                    trimTrailingZeros: true,
                    unit: 'MWh',
                    urn: [savedEnergySensorName, energyAmountTrainSensorName],
                },
                {
                    aggregation: 'last' as const,
                    conversion: 1 / 1000,
                    fractionDigits: 2,
                    key: 'saved_energy_last_month',
                    legendConversion: 100,
                    legendFractionDigits: 1,
                    legendUnit: '%',
                    lookback: { amount: 1, toleranceDays: 7, unit: 'month' as const },
                    noDataMessage: t('No historical comparison value is available.'),
                    resolveTrafficLight: ({ comparisonDate, valueDate }: KpiCarouselTrafficLightContext) => (
                        createSavedEnergyTrafficLight(comparisonDate, valueDate)
                    ),
                    resolveTrafficLightValue: ({ value, values }: KpiCarouselTrafficLightContext) => (
                        getSavedEnergyRatio(value, values[energyAmountTrainSensorName] ?? null)
                    ),
                    title: t('Saved energy last month'),
                    transformValue: (value: number) => roundToSignificantDigits(value, 2),
                    trimTrailingZeros: true,
                    unit: 'MWh',
                    urn: [savedEnergySensorName, energyAmountTrainSensorName],
                },
            ],
        },
    };
}
